import {AuthModel} from './_models'

// «ثبت‌نام آنلاین و ساخت پنل» (2026-10-18): any signed-in user makes the panel of a business they
// prove to own. The tax organisation sends its delegation OTP to the mobile registered in the
// taxpayer's کارپوشه; entering it proves the ownership and gives ASA its access, then the fiscal
// memory id is issued through the organisation's API (or an existing one is checked and added).
// The backend owns every rule (OnlineRegistrationRules); these helpers only shape the page.

export const ONLINE_REGISTRATION_PATH = '/taxpayer/online-register'
export const CONSULTATION_PATH = '/register/login'

export type OnlineStep = 'identify' | 'owner' | 'memory' | 'done'

export const ONLINE_STEPS: {key: OnlineStep; title: string}[] = [
  {key: 'identify', title: 'شناسایی کسب‌وکار'},
  {key: 'owner', title: 'تأیید مالک'},
  {key: 'memory', title: 'شناسه یکتا'},
  {key: 'done', title: 'پایان'},
]

export interface OnlineAvailability {
  available: boolean
  reason?: string
  message?: string
  environment?: string
  memoryUniqueIdAvailable: boolean
  ssoIdentifier?: string
  permissions: string[]
}

export type PanelState = 'none' | 'yours' | 'other'

export interface OnlineTaxFile {
  economicCode: string
  nationalId?: string
  brand?: string
  taxFileStatus?: string
  taxFileStatusTitle?: string
  callStatusTitle?: string
  selectable: boolean
  reason?: string
  panelState: PanelState
  companyId?: number
  companyName?: string
}

export interface OnlineIdentifyResult {
  queriedBy: 'economicCode' | 'nationalId'
  fromSsoIdentity: boolean
  items: OnlineTaxFile[]
}

export interface OnlineDelegation {
  status?: string
  usable: boolean
  otpPending: boolean
  maskedMobile?: string
  otpLength?: number
  otpExpiresAt?: number
  expiresAt?: number
  memoryPermissionGranted: boolean
}

export interface OnlineSession {
  inquiryId: string
  expiresAtUtc: string
  mode: 'create' | 'complete'
  companyId?: number
  companyName?: string
  taxpayerName: string
  taxpayerType: number
  taxpayerTypeTitle: string
  economicCode: string
  nationalIdentifier: string
  taxFileStatusTitle?: string
  delegation: OnlineDelegation
  memoryUniqueIdAvailable: boolean
  hasFiscalMemory: boolean
  otpRequired: boolean
  permissions: string[]
}

export interface OnlineVerifyResult {
  companyId: number
  companyName: string
  created: boolean
  replayed: boolean
  delegation: OnlineDelegation
  memoryUniqueIdAvailable: boolean
  hasFiscalMemory: boolean
}

export interface OnlineMemoryResult {
  companyId: number
  memoryUniqueId?: string
  companyTokenId?: number
  issued: boolean
  environment?: string
  message?: string
}

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹'
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩'

export const toLatinDigits = (value: string): string =>
  (value || '')
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))

/** Digits only (Persian and Arabic digits become Latin), at most 14. */
export const normalizeIdentifierInput = (value: string): string =>
  toLatinDigits(value).replace(/[^0-9]/g, '').slice(0, 14)

/** What a typed identifier is, for the hint under the field; null while it is not one yet. */
export const describeIdentifier = (digits: string): string | null => {
  switch (digits.length) {
    case 14:
      return 'شماره اقتصادی ۱۴ رقمی (حقیقی یا اتباع)'
    case 11:
      return 'شماره اقتصادی یا شناسه ملی ۱۱ رقمی (حقوقی یا مشارکت مدنی)'
    case 10:
      return 'کد ملی؛ همهٔ پرونده‌های مالیاتی این شخص فهرست می‌شود'
    case 12:
      return 'کد فراگیر اتباع'
    default:
      return null
  }
}

export const isSearchableIdentifier = (digits: string): boolean => describeIdentifier(digits) !== null

export const normalizeOtpInput = (value: string, length = 6): string =>
  toLatinDigits(value)
    .replace(/[^0-9]/g, '')
    .slice(0, Math.min(Math.max(length || 6, 4), 12))

export const isCompleteOtp = (value: string, length = 6): boolean =>
  value.length === Math.min(Math.max(length || 6, 4), 12) && /^[0-9]+$/.test(value)

/** The step after a business was chosen: the owner's code, the memory id, or nothing left. */
export const stepAfterSession = (session: OnlineSession): OnlineStep => {
  if (session.otpRequired) return 'owner'
  return session.hasFiscalMemory ? 'done' : 'memory'
}

export const stepAfterVerify = (result: OnlineVerifyResult): OnlineStep =>
  result.hasFiscalMemory ? 'done' : 'memory'

export const stepIndex = (step: OnlineStep): number =>
  ONLINE_STEPS.findIndex((item) => item.key === step)

export const toPersianNumber = (value: number | string): string =>
  String(value).replace(/[0-9]/g, (digit) => PERSIAN_DIGITS[Number(digit)])

/** «۱:۵۸» for the time left on a code; «۰:۰۰» once it is gone. */
export const formatRemaining = (milliseconds: number): string => {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000))
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return toPersianNumber(`${minutes}:${rest.toString().padStart(2, '0')}`)
}

/** A Jalali date for an epoch in milliseconds, «۲۶ مهر ۱۴۰۶». */
export const formatJalaliDate = (epochMilliseconds?: number): string | null => {
  if (!epochMilliseconds) return null
  try {
    return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
      timeZone: 'Asia/Tehran',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(new Date(epochMilliseconds))
  } catch {
    return null
  }
}

export const normalizeFiscalId = (value: string): string =>
  toLatinDigits(value)
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .slice(0, 6)

export const isCompleteFiscalIdValue = (value: string): boolean => /^[0-9A-Z]{6}$/.test(value)

type OnlineUser = Partial<Pick<AuthModel, 'sso' | 'rl' | 'support' | 'cpl'>> | null | undefined

// Role -1 alone is not enough: a session still on a business the user has left reads -1 too, with the others listed.
export const hasNoBusiness = (user: OnlineUser): boolean => user?.rl === -1 && !user?.cpl?.length

export const isSupportSession = (user: OnlineUser): boolean =>
  user?.support?.session_type === 'support'

export interface OnlineFailure {
  code?: string
  message: string
  traceId?: string
  existingBusiness?: {companyId: number; name: string}
  /** The session is gone or no longer valid: choose the business again. */
  restart: boolean
  /** The code is gone: ask for a new one. */
  newCode: boolean
  /** The business belongs to someone else or the data is inconsistent: the support line helps. */
  contactSupport: boolean
  /** The service is not offered (or the backend does not know it yet). */
  unavailable: boolean
}

const RESTART_CODES = new Set([
  'inquiry_expired',
  'inquiry_environment_changed',
  'inquiry_consumed',
  'inquiry_not_found',
  'inquiry_invalid',
  'online_business_exists',
])

const NEW_CODE_CODES = new Set(['otp_expired', 'otp_attempts_exceeded', 'delegation_required'])

const SUPPORT_CODES = new Set([
  'online_business_exists',
  'identity_mismatch',
  'fiscal_id_in_use',
  'organization_access_closed',
])

type ApiErrorBody = {
  code?: string
  message?: string
  traceId?: string
  existingBusiness?: {companyId: number; name: string}
}

export const readOnlineFailure = (error: unknown, fallback: string): OnlineFailure => {
  const response = (error as {response?: {status?: number; data?: ApiErrorBody}})?.response
  if (!response) {
    return {
      message: 'ارتباط با سرور برقرار نشد. اینترنت خود را بررسی و دوباره تلاش کنید.',
      restart: false,
      newCode: false,
      contactSupport: false,
      unavailable: false,
    }
  }

  const body = response.data && typeof response.data === 'object' ? response.data : {}
  const code = body.code
  // A backend without the online routes answers 404 with no code of ours.
  const unavailable =
    code === 'online_registration_unavailable' ||
    code === 'online_registration_forbidden' ||
    (response.status === 404 && !code)
  return {
    code,
    message: unavailable && !body.message ? 'ثبت‌نام آنلاین در حال حاضر در دسترس نیست.' : body.message || fallback,
    traceId: body.traceId,
    existingBusiness: code === 'online_business_already_yours' ? body.existingBusiness : undefined,
    restart: Boolean(code && RESTART_CODES.has(code)),
    newCode: Boolean(code && NEW_CODE_CODES.has(code)),
    contactSupport: Boolean(code && SUPPORT_CODES.has(code)),
    unavailable,
  }
}
